<div class="connections-header">
  <h2>Connections</h2>
  <span [matTooltip]="addPayrollConnectionTooltip">
    <button
      class="button add-connection-button"
      color="primary"
      mat-raised-button
      [disabled]="isLoading || hasPayrollConnectionLimitReached"
      (click)="addConnection()"
    >
      <mat-icon>add</mat-icon>
      Add Connection
    </button>
  </span>
</div>

<section class="connection-section">
  <div class="section-title">
    <mat-icon>account_balance</mat-icon>
    <span>Payroll Connections</span>
  </div>

  <div class="table-shell" *ngIf="payrollConnections.length > 0; else emptyPayrollConnections">
    <table class="connections-table desktop-table">
      <thead>
        <tr>
          <th>Name</th>
          <th>Type</th>
          <th>System ID</th>
          <th>Status</th>
          <th>Last Read</th>
          <th>Connected</th>
          <th class="actions-col">Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let row of payrollConnections">
          <td>{{ row.name }}</td>
          <td>{{ row.type }}</td>
          <td>{{ row.systemId }}</td>
          <td>
            <span
              class="status-pill"
              [class.connected]="row.status === 'Connected'"
              [class.error]="row.status === 'Error' || row.status === 'Unsupported'"
              [matTooltip]="row.healthTooltip || row.setupUnavailableReason"
            >
              {{ row.status }}
            </span>
          </td>
          <td>{{ row.lastReadAt ? (row.lastReadAt | date:'short') : '--' }}</td>
          <td>{{ row.connectedOn | date:'mediumDate' }}</td>
          <td class="actions-col">
            <ng-container *ngIf="row.canManageSetup">
              <button
                *ngIf="canRefreshHealth(row)"
                mat-icon-button
                type="button"
                aria-label="Refresh payroll connection health"
                matTooltip="Verify Paycor connection"
                [disabled]="row.isRefreshingHealth"
                (click)="refreshPaycorConnectionHealth(row)"
              >
                <mat-icon>{{ row.isRefreshingHealth ? 'hourglass_empty' : 'refresh' }}</mat-icon>
              </button>
              <button mat-icon-button type="button" aria-label="Edit payroll connection" (click)="editPayrollConnection(row)">
                <mat-icon>edit</mat-icon>
              </button>
              <button mat-icon-button type="button" aria-label="Delete payroll connection" (click)="removePayrollConnection(row)">
                <mat-icon>delete</mat-icon>
              </button>
            </ng-container>
          </td>
        </tr>
      </tbody>
    </table>

    <div class="mobile-connections-list" aria-label="Payroll connections">
      <article class="mobile-connection-card" *ngFor="let row of payrollConnections">
        <div class="mobile-card-top">
          <div class="mobile-card-title">{{ row.name }}</div>
          <div class="mobile-card-actions" *ngIf="row.canManageSetup">
            <button
              *ngIf="canRefreshHealth(row)"
              mat-icon-button
              type="button"
              aria-label="Refresh payroll connection health"
              matTooltip="Verify Paycor connection"
              [disabled]="row.isRefreshingHealth"
              (click)="refreshPaycorConnectionHealth(row)"
            >
              <mat-icon>{{ row.isRefreshingHealth ? 'hourglass_empty' : 'refresh' }}</mat-icon>
            </button>
            <button mat-icon-button type="button" aria-label="Edit payroll connection" (click)="editPayrollConnection(row)">
              <mat-icon>edit</mat-icon>
            </button>
            <button mat-icon-button type="button" aria-label="Delete payroll connection" (click)="removePayrollConnection(row)">
              <mat-icon>delete</mat-icon>
            </button>
          </div>
        </div>

        <div class="mobile-card-summary">
          <span
            class="status-pill"
            [class.connected]="row.status === 'Connected'"
            [class.error]="row.status === 'Error' || row.status === 'Unsupported'"
            [matTooltip]="row.healthTooltip || row.setupUnavailableReason"
          >
            {{ row.status }}
          </span>
          <span class="mobile-connected-date">{{ row.connectedOn | date:'mediumDate' }}</span>
        </div>

        <details class="mobile-card-details">
          <summary>View details</summary>
          <div class="mobile-field-row">
            <span class="mobile-field-label">Type</span>
            <span class="mobile-field-value">{{ row.type }}</span>
          </div>
          <div class="mobile-field-row">
            <span class="mobile-field-label">System ID</span>
            <span class="mobile-field-value">{{ row.systemId }}</span>
          </div>
          <div class="mobile-field-row">
            <span class="mobile-field-label">Last Read</span>
            <span class="mobile-field-value">{{ row.lastReadAt ? (row.lastReadAt | date:'short') : '--' }}</span>
          </div>
        </details>
      </article>
    </div>
  </div>

  <ng-template #emptyPayrollConnections>
    <div class="empty-table-state empty-payroll-state">
      <div>No payroll connections configured</div>
      <button mat-raised-button color="primary" type="button" (click)="addConnection()">
        <mat-icon>add</mat-icon>
        Add Connection
      </button>
    </div>
  </ng-template>
</section>

<section class="connection-section">
  <div class="section-title">
    <mat-icon>credit_card</mat-icon>
    <span>Bank Connections</span>
  </div>

  <div class="table-shell" *ngIf="bankConnections.length > 0; else emptyBankConnections">
    <table class="connections-table desktop-table">
      <thead>
        <tr>
          <th>Name</th>
          <th>Bank</th>
          <th>Account</th>
          <th>Teams</th>
          <th>Status</th>
          <th>Connected</th>
          <th class="actions-col">Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let row of bankConnections">
          <td>{{ row.name }}</td>
          <td>{{ row.bank }}</td>
          <td>{{ row.account }}</td>
          <td>
            <span class="teams-chip" [matTooltip]="row.teamNames.length > 0 ? row.teamNames.join(', ') : row.fundingSource" matTooltipPosition="above">
              {{ row.teamNames.length === 0 ? row.fundingSource : (row.teamNames.length > 2 ? (row.teamNames[0] + ', ' + row.teamNames[1] + ' +' + (row.teamNames.length - 2)) : row.teamNames.join(', ')) }}
            </span>
          </td>
          <td>
            <span class="status-pill" [class.connected]="row.status === 'Connected'" [class.error]="row.status === 'Error'">{{ row.status }}</span>
          </td>
          <td>{{ row.connectedOn | date:'mediumDate' }}</td>
          <td class="actions-col">
            <button mat-icon-button type="button" aria-label="Delete bank connection" (click)="removeBankConnection(row)">
              <mat-icon>delete</mat-icon>
            </button>
          </td>
        </tr>
      </tbody>
    </table>

    <div class="mobile-connections-list" aria-label="Bank connections">
      <article class="mobile-connection-card" *ngFor="let row of bankConnections">
        <div class="mobile-card-top">
          <div class="mobile-card-title">{{ row.name }}</div>
          <div class="mobile-card-actions">
            <button mat-icon-button type="button" aria-label="Delete bank connection" (click)="removeBankConnection(row)">
              <mat-icon>delete</mat-icon>
            </button>
          </div>
        </div>

        <div class="mobile-card-summary">
          <span class="status-pill" [class.connected]="row.status === 'Connected'" [class.error]="row.status === 'Error'">{{ row.status }}</span>
          <span class="mobile-connected-date">{{ row.connectedOn | date:'mediumDate' }}</span>
        </div>

        <details class="mobile-card-details">
          <summary>View details</summary>
          <div class="mobile-field-row">
            <span class="mobile-field-label">Bank</span>
            <span class="mobile-field-value">{{ row.bank }}</span>
          </div>
          <div class="mobile-field-row">
            <span class="mobile-field-label">Account</span>
            <span class="mobile-field-value">{{ row.account }}</span>
          </div>
          <div class="mobile-field-row">
            <span class="mobile-field-label">Teams</span>
            <span class="mobile-field-value">{{ row.teamNames.length > 0 ? row.teamNames.join(', ') : row.fundingSource }}</span>
          </div>
        </details>
      </article>
    </div>
  </div>

  <ng-template #emptyBankConnections>
    <div class="empty-table-state empty-bank-state" *ngIf="isLoadingBankConnections; else noBankConnections">
      <mat-spinner [diameter]="26"></mat-spinner>
      <div>Loading bank connections</div>
    </div>

    <ng-template #noBankConnections>
    <div class="empty-table-state empty-bank-state">
      <div>No bank connections configured</div>
      <button mat-raised-button color="primary" type="button" [disabled]="isLoadingBankConnections" (click)="addBankConnection()">
        <mat-icon>add</mat-icon>
        Add Bank Connection
      </button>
    </div>
    </ng-template>
  </ng-template>
</section>

<section class="connection-section" *ngIf="hasDeductionCodeInspection">
  <div class="collapsible-section-header">
    <div class="section-title">
      <mat-icon>visibility</mat-icon>
      <span>Deduction Code Inspection</span>
    </div>

    <button
      mat-icon-button
      type="button"
      class="collapse-toggle"
      [attr.aria-label]="isDeductionCodeInspectionExpanded ? 'Collapse deduction code inspection' : 'Expand deduction code inspection'"
      [attr.aria-expanded]="isDeductionCodeInspectionExpanded"
      aria-controls="deduction-code-inspection-panel"
      (click)="toggleDeductionCodeInspection()"
    >
      <mat-icon>{{ isDeductionCodeInspectionExpanded ? 'expand_less' : 'expand_more' }}</mat-icon>
    </button>
  </div>

  <div id="deduction-code-inspection-panel" *ngIf="isDeductionCodeInspectionExpanded">
    <div class="inspection-note">
      {{ deductionCodeInspectionCopy }}
    </div>

    <div class="table-shell inspection-shell">
      <div class="inspection-loading" *ngIf="isLoadingDeductionCodes">
        <mat-spinner [diameter]="26"></mat-spinner>
        <span>Loading deduction codes</span>
      </div>

      <div class="inspection-message inspection-message--error" *ngIf="!isLoadingDeductionCodes && deductionCodeInspectionError">
        <mat-icon>warning_amber</mat-icon>
        <span>{{ deductionCodeInspectionError }}</span>
      </div>

      <div class="inspection-message" *ngIf="!isLoadingDeductionCodes && !deductionCodeInspectionError && deductionCodeInspectionRows.length === 0">
        No deduction codes returned by this payroll provider.
      </div>

      <ng-container *ngIf="!isLoadingDeductionCodes && !deductionCodeInspectionError && deductionCodeInspectionRows.length > 0">
        <table class="connections-table desktop-table">
          <thead>
            <tr>
              <th>Code</th>
              <th *ngIf="showDeductionCodeNameColumn">Name</th>
              <th *ngIf="showDeductionCodeDescriptionColumn">Description</th>
              <th *ngIf="showDeductionCodeStatusColumn">Status</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let row of deductionCodeInspectionRows">
              <td>{{ row.code || '--' }}</td>
              <td *ngIf="showDeductionCodeNameColumn">{{ row.name || '--' }}</td>
              <td *ngIf="showDeductionCodeDescriptionColumn">{{ row.description || '--' }}</td>
              <td *ngIf="showDeductionCodeStatusColumn">
                <span class="status-pill" *ngIf="row.status">{{ row.status }}</span>
                <span *ngIf="!row.status">--</span>
              </td>
            </tr>
          </tbody>
        </table>

        <div class="mobile-connections-list" aria-label="Deduction code inspection">
          <article class="mobile-connection-card" *ngFor="let row of deductionCodeInspectionRows">
            <div class="mobile-card-top">
              <div class="mobile-card-title">{{ row.code || row.name || 'Deduction code' }}</div>
              <span class="status-pill" *ngIf="row.status">{{ row.status }}</span>
            </div>

            <details class="mobile-card-details">
              <summary>View details</summary>
              <div class="mobile-field-row" *ngIf="showDeductionCodeNameColumn">
                <span class="mobile-field-label">Name</span>
                <span class="mobile-field-value">{{ row.name || '--' }}</span>
              </div>
              <div class="mobile-field-row" *ngIf="showDeductionCodeDescriptionColumn">
                <span class="mobile-field-label">Description</span>
                <span class="mobile-field-value">{{ row.description || '--' }}</span>
              </div>
              <div class="mobile-field-row" *ngIf="showDeductionCodeStatusColumn">
                <span class="mobile-field-label">Status</span>
                <span class="mobile-field-value">{{ row.status || '--' }}</span>
              </div>
            </details>
          </article>
        </div>
      </ng-container>
    </div>
  </div>
</section>
